import { transform, update } from "lodash";
import { Button } from "PFComponents/button";
import { FilterFamily } from "PFTypes";
import { FiltersConfigElement, FiltersConfigFormData } from "PFTypes/filters_config";
import React, { useCallback, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { OptionalSection, Section } from "../components/section";
import { useFiltersAndOrderConfigContext } from "../filters_and_order_config_context";
import css from "./config_form.module.scss";

export const FiltersConfigForm = () => {
  const { t } = useTranslation("saasAdmin", { keyPrefix: "filtersConfig" });
  const { t: tTranslation } = useTranslation();
  const history = useHistory();
  const {
    target,
    config: { data: config },
    template: { data: configTemplate },
    handleSubmit: onSubmit,
    handleError: onError
  } = useFiltersAndOrderConfigContext().filters;

  const getFilterFamilyArray = useCallback(
    (filterFamily: FilterFamily) => {
      if (!config) {
        return [];
      }
      return transform(
        config[filterFamily] || {},
        (result: FiltersConfigElement[], value: FiltersConfigElement, key: FilterFamily) =>
          result.push({ ...value, name: key }),
        []
      );
    },
    [config]
  );

  const childrenTemplate = useMemo(() => {
    if (!configTemplate) {
      return {};
    }
    const template = { ...configTemplate.children };
    return update(template, "inputs.target.options", (options) =>
      (options || []).filter((childrenTarget) => childrenTarget !== target)
    );
  }, [configTemplate, target]);

  const defaultValues = useMemo(() => {
    if (!config) {
      return {};
    }
    return {
      fields: getFilterFamilyArray("fields"),
      numbers: getFilterFamilyArray("numbers"),
      strings: getFilterFamilyArray("strings"),
      locations: getFilterFamilyArray("locations"),
      availability: config.availability ? [config.availability] : [],
      children: config.children ? [{ target: config.children }] : []
    };
  }, [config, getFilterFamilyArray]);

  const methods = useForm<FiltersConfigFormData>({
    defaultValues
  });

  const { handleSubmit, reset } = methods;

  if (!configTemplate) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <Section filterFamily="fields" template={configTemplate.fields} />
        <Section filterFamily="numbers" template={configTemplate.numbers} />
        <Section filterFamily="strings" template={configTemplate.strings} />
        {configTemplate.locations && <Section filterFamily="locations" template={configTemplate.locations} />}
        <OptionalSection template={configTemplate.availability} filterFamily="availability" />
        <OptionalSection template={childrenTemplate} filterFamily="children" />
        <div className={css.buttons}>
          <Button
            type="button"
            kind="secondary"
            text={tTranslation("cancel")}
            onClick={() => history.push("/saas_admin/accounts")}
          />
          <Button
            type="button"
            kind="secondary"
            text={tTranslation("reset")}
            onClick={() => reset(undefined, { keepDefaultValues: true })}
          />
          <Button type="submit" text={t("submit")} />
        </div>
      </form>
    </FormProvider>
  );
};
